import { Bonus, FeaturedBonusInterface } from '@/app/rewards/types';
import { OrderedClaimedRewardsData } from '@/backend/bonus/ordered-claims/get-ordered-claimed-rewards';
import { create } from 'zustand';
import { useShallow } from 'zustand/shallow';

/* -------------------------------------------------------------------------- */
export enum StepEnum {
	EnterPromoCode = 'enterPromoCode',
	SelectCurrency = 'selectCurrency',
}

interface RewardStore {
	bonusInWalletModal: string | null;
	redeemCodeModalStep: StepEnum;
	redeemCode: string;
	forfeitBonusModalId: string | null;
	offerRewards: {
		featuredBonus: FeaturedBonusInterface | null;
		bonuses: Bonus[] | null;
	} | null;
	claimedRewards: {
		bonuses: Bonus[] | null;
		claimed: number;
	} | null;
	claimedBonusMoney: OrderedClaimedRewardsData | null;
	claimedSpins: OrderedClaimedRewardsData | null;

	actions: {
		setOfferRewards: (offerRewards: {
			featuredBonus: FeaturedBonusInterface | null;
			bonuses: Bonus[] | null;
		}) => void;
		setClaimedRewards: (claimedRewards: {
			bonuses: Bonus[] | null;
			claimed: number;
		}) => void;
		setClaimedBonusMoney: (
			claimedBonusMoney: OrderedClaimedRewardsData | null
		) => void;
		setClaimedSpins: (claimedSpins: OrderedClaimedRewardsData | null) => void;
		setBonusInWalletModal: (bonusId: string) => void;
		setRedeemCodeModalStep: (step: StepEnum) => void;
		setRedeemCode: (code: string) => void;
		resetRedeemModal: () => void;
		setForfeitBonusModalId: (id: string | null) => void;
		resetStore: () => void;
	};
}

const initialState = {
	offerRewards: null,
	claimedRewards: null,
	claimedBonusMoney: null,
	claimedSpins: null,
	modalStatus: null,
	bonusInWalletModal: null,
	redeemCodeModalStep: StepEnum.EnterPromoCode,
	redeemCode: '',
	forfeitBonusModalId: null,
};

const rewardStore = create<RewardStore>((set) => ({
	...initialState,

	actions: {
		resetRedeemModal: () => {
			set(() => ({
				redeemCodeModalStep: StepEnum.EnterPromoCode,
				redeemCode: '',
			}));
		},
		setOfferRewards: (offerRewards) => {
			set(() => ({
				offerRewards,
			}));
		},
		setClaimedRewards: (claimedRewards) => {
			set(() => ({
				claimedRewards,
			}));
		},
		setClaimedBonusMoney: (claimedBonusMoney) => {
			set(() => ({
				claimedBonusMoney,
			}));
		},
		setClaimedSpins: (claimedSpins) => {
			set(() => ({
				claimedSpins,
			}));
		},
		setRedeemCodeModalStep: (step: StepEnum) => {
			set(() => ({
				redeemCodeModalStep: step,
			}));
		},
		setBonusInWalletModal: (bonusId) => {
			set(() => ({
				bonusInWalletModal: bonusId,
			}));
		},
		setRedeemCode: (code: string) => {
			set(() => ({
				redeemCode: code,
			}));
		},
		setForfeitBonusModalId: (id: string | null) => {
			set(() => ({
				forfeitBonusModalId: id,
			}));
		},

		resetStore: () => {
			set(() => ({
				...initialState,
			}));
		},
	},
}));

export const useOfferRewards = () =>
	rewardStore(useShallow((state) => state.offerRewards));

export const useClaimedRewards = () =>
	rewardStore(useShallow((state) => state.claimedRewards));

export const useClaimedBonusMoney = () =>
	rewardStore(useShallow((state) => state.claimedBonusMoney));

export const useClaimedSpins = () =>
	rewardStore(useShallow((state) => state.claimedSpins));

export const useRedeemCodeModalStep = () =>
	rewardStore(useShallow((state) => state.redeemCodeModalStep));

export const useBonusInWalletModal = () =>
	rewardStore(useShallow((state) => state.bonusInWalletModal));

export const useRedeemCode = () =>
	rewardStore(useShallow((state) => state.redeemCode));

export const useForfeitBonusModalId = () =>
	rewardStore(useShallow((state) => state.forfeitBonusModalId));

export const useRewardStoreActions = () =>
	rewardStore((state) => state.actions);
